import getPriceBillingConfig from '../lib/getPriceBillingConfig';

// Fixed canonical tab order per grouping axis. Tabs always render in this
// order regardless of the order prices appear in the product; a seller's
// saved `priceDisplayTabOrder` is layered on top by the consumers. Every
// label a `groupLabelFor(_, axis)` can return MUST appear here.
export const GROUP_ORDER = {
  billing: ['One-time', 'Subscription'],
  interval: ['One-off', 'Daily', 'Weekly', 'Monthly', 'Quarterly', 'Semi-annual', 'Yearly'],
};

export function groupLabelFor(price, axis) {
  const isRecurring = price?.billingType === 'recurring';
  if (axis === 'billing') return isRecurring ? 'Subscription' : 'One-time';
  if (axis === 'interval') {
    if (!isRecurring) return 'One-off';
    const config = getPriceBillingConfig(price);
    const interval = config?.interval || 'month';
    const count = config?.intervalCount || 1;
    if (interval === 'day') return 'Daily';
    if (interval === 'week') return 'Weekly';
    if (interval === 'year') return 'Yearly';
    if (count === 3) return 'Quarterly';
    if (count === 6) return 'Semi-annual';
    return 'Monthly';
  }
  return null;
}

export function derivedGroups(prices = [], axis) {
  if (axis !== 'billing' && axis !== 'interval') return [];
  const order = GROUP_ORDER[axis];
  const present = new Set();
  for (const p of prices) {
    const g = groupLabelFor(p, axis);
    if (g) present.add(g);
  }

  const known = order.filter(g => present.has(g));
  const extras = [...present].filter(g => !order.includes(g));
  return [...known, ...extras];
}

/**
 * The grouping a price set naturally falls into — the render-time engine for
 * the `auto` groupBy value. Mirrors the (retired) server
 * resolveDefaultPricePickerGrouping, built on the same derivedGroups the
 * picker renders from so `auto` never resolves to an axis with a single tab:
 *   one-time + recurring              -> 'billing'
 *   all recurring, 2+ interval groups -> 'interval'
 *   anything else                     -> null
 * and then null unless every tab of that axis holds 2+ prices.
 *
 * Hidden prices are treated as non-existing here: the axis is derived from the
 * visible prices only. A hidden price revealed via a query param keeps its
 * `hidden` flag, so it never changes the axis — it just renders within whatever
 * mode the visible prices dictate (e.g. a revealed one-time appears in interval
 * mode when the visible prices are all subscriptions).
 */
export function resolveAutoGroupBy(prices = []) {
  const enabled = (prices ?? []).filter(p => p?.enabled !== false && p?.hidden !== true);
  const hasOneTime = enabled.some(p => p.billingType === 'one_time');
  const hasRecurring = enabled.some(p => p.billingType === 'recurring');
  let axis = null;
  if (hasOneTime && hasRecurring) axis = 'billing';
  else if (hasRecurring && derivedGroups(enabled, 'interval').length >= 2) axis = 'interval';
  if (!axis) return null;

  // A tab holding a single price only hides it behind a click and stops the
  // customer comparing it, so Auto tabs only when every tab offers a choice.
  const counts = new Map();
  for (const p of enabled) {
    const g = groupLabelFor(p, axis);
    counts.set(g, (counts.get(g) ?? 0) + 1);
  }
  return [...counts.values()].every(n => n >= 2) ? axis : null;
}
